/* eslint-disable import/prefer-default-export */
import { waitForOfficeInitialization } from './officeInitialization';

export enum DialogMessageStatus {
  Success = 'success',
  Error = 'error',
  // OIDC sign-in flow
  DialogReadyForToken = 'dialog_ready_for_token',
  SignoutComplete = 'signout_complete',
}

export interface DialogMessageEvent {
  message: string;
  origin: string | undefined;
}

export interface DialogMessage {
  status?: DialogMessageStatus;
  message?: string;
  error?: string;
  // OIDC authentication
  oidcUserStorageString?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface DialogController {
  dialog: Office.Dialog;
  resolve: (msg?: DialogMessage) => void;
  reject: (error: Error) => void;
}

export interface CreateDialogOptions {
  path: string;
  onMessage?: OnMessageHandler;
}

const CLOSED_BY_USER_ERROR_CODE = 12006;

type OnMessageHandler = (msg: DialogMessage, controller: DialogController) => void | Promise<void>;

interface DialogErrorEvent {
  error: number;
}

interface DialogRequest {
  path: string;
  resolve: (msg: DialogMessage) => void;
  reject: (error: Error) => void;
  onMessage?: OnMessageHandler;
}

const isDialogErrorEvent = (arg: unknown): arg is DialogErrorEvent =>
  typeof arg === 'object'
  && arg !== null
  && 'error' in (arg as any)
  && typeof (arg as any).error === 'number';

export const getDialogDimensions = (desiredWidth: number = 500, desiredHeight: number = 700) => {
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;

  if (screenWidth <= 0 || screenHeight <= 0) {
    return { width: 50, height: 70 };
  }

  const widthPercent = Math.min(100, Math.max(30, Math.round((desiredWidth / screenWidth) * 100)));
  const heightPercent = Math.min(100, Math.max(40, Math.round((desiredHeight / screenHeight) * 100)));

  return { width: widthPercent, height: heightPercent };
};

export class OfficeNotReadyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OfficeNotReadyError';
  }
}

const ensureDialogApiAvailable = (): void => {
  if (!Office?.context) {
    throw new OfficeNotReadyError('Office context is not available');
  }
  if (!Office?.context?.ui) {
    throw new OfficeNotReadyError('Office.context.ui is not available in this context');
  }
  if (typeof Office.context.ui.displayDialogAsync !== 'function') {
    throw new OfficeNotReadyError('displayDialogAsync is not supported in this Office host');
  }
};

const ensureMessageParentAvailable = (): void => {
  if (!Office?.context?.ui) {
    throw new OfficeNotReadyError('Office.context.ui is not available in this context');
  }
  if (typeof Office.context.ui.messageParent !== 'function') {
    throw new OfficeNotReadyError('messageParent is not available - this code must run inside a dialog');
  }
};

const dialogQueue: DialogRequest[] = [];
let currentDialogPath: string | null = null;

const openOfficeDialog = (
  path: string,
  onMessage?: OnMessageHandler,
): Promise<DialogMessage> =>
  new Promise((resolve, reject) => {
    try {
      ensureDialogApiAvailable();

      Office.context.ui.displayDialogAsync(
        `${window.location.origin}/${path}`,
        { ...getDialogDimensions(), promptBeforeOpen: false },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            const err = result.error;
            reject(new Error(`Failed to open dialog: ${err?.message ?? 'Unknown error'} (${err?.code ?? 'no-code'})`));
            return;
          }

          const dialog = result.value;

          dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg) => {
            if (isDialogErrorEvent(arg)) {
              dialog.close();
              reject(new Error('DialogMessageReceived error: ' + (arg as DialogErrorEvent).error.toString()));
              return;
            }

            try {
              const { message, origin } = arg as DialogMessageEvent;
              if (origin && origin !== window.location.origin) {
                dialog.close();
                reject(new Error(`Unexpected dialog message origin: ${origin}`));
                return;
              }
              const msg = JSON.parse(message);

              // if onMessage handler is provided, use it for custom handling
              if (onMessage) {
                const controller: DialogController = {
                  dialog,
                  resolve: (res?: DialogMessage) => {
                    dialog.close();
                    resolve(res || { status: DialogMessageStatus.Success });
                  },
                  reject: (error: Error) => {
                    dialog.close();
                    reject(error);
                  },
                };
                await onMessage(msg, controller);
                return;
              }

              // Default handling for standard dialog messages
              dialog.close();
              if (msg.status === DialogMessageStatus.Success) {
                resolve(msg);
              } else if (msg.status === DialogMessageStatus.Error) {
                reject(new Error(msg.message ?? msg.error ?? 'Unknown error'));
              } else {
                reject(new Error('DialogMessageReceived error: unknown or missing status'));
              }
            } catch (error) {
              dialog.close();
              reject(new Error('DialogMessageReceived error: ' + error));
            }
          });

          // Handle dialog closed
          dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
            if (isDialogErrorEvent(arg)) {
              const code = (arg as DialogErrorEvent).error;
              if (code === CLOSED_BY_USER_ERROR_CODE) {
                reject(new Error('Dialog closed by user'));
              } else {
                reject(new Error(`Dialog event error: ${code}`));
              }
            }
          });
        },
      );
    } catch (error) {
      reject(new Error(`Error from dialog: ${error}`));
    }
  });

const processQueue = () => {
  // If dialog already open or queue empty, do nothing
  if (currentDialogPath !== null || dialogQueue.length === 0) return;
  const request = dialogQueue.shift()!;
  currentDialogPath = request.path;

  openOfficeDialog(request.path, request.onMessage)
    .then((msg) => {
      currentDialogPath = null;
      request.resolve(msg);
      processQueue();
    })
    .catch((error) => {
      currentDialogPath = null;
      request.reject(error);
      processQueue();
    });
};

export const createDialog = (pathOrOptions: string | CreateDialogOptions): Promise<DialogMessage> => {
  return new Promise((resolve, reject) => {
    const options = typeof pathOrOptions === 'string' ? { path: pathOrOptions } : pathOrOptions;
    dialogQueue.push({ path: options.path, resolve, reject, onMessage: options.onMessage });
    processQueue();
  });
};

export const dialog = async (callback: () => Promise<void> | void): Promise<void> => {
  try {
    await waitForOfficeInitialization();
    ensureMessageParentAvailable();
    await callback();
  } catch (error) {
    if (error instanceof OfficeNotReadyError) {
      throw error;
    }
    Office.context.ui.messageParent(
      JSON.stringify({ status: DialogMessageStatus.Error, message: error instanceof Error ? error.message : 'Unknown error' }),
    );
    throw error;
  }
};

// Helper to send message to parent from dialog
export const sendMessageToParent = async (message: DialogMessage): Promise<void> => {
  await waitForOfficeInitialization();
  ensureMessageParentAvailable();
  Office.context.ui.messageParent(JSON.stringify(message));
};

// Helper to add message handler in dialog for parent messages
export const onParentMessage = async (handler: (message: DialogMessage) => void | Promise<void>): Promise<void> => {
  await waitForOfficeInitialization();
  Office.context.ui.addHandlerAsync(Office.EventType.DialogParentMessageReceived, async (arg: { message: string }) => {
    const message = JSON.parse(arg.message);
    await handler(message);
  });
};